/**
 * /superadmin/users — cross-tenant person (identity) lookup for "I
 * can't log in" tickets.
 *
 * Search by exact email OR a name/email substring (?email= / ?q=), maps
 * to GET /api/v1/admin/users. Each result is ONE person (identity) with
 * every tenant membership attached, plus the credential-posture flags
 * (2FA enrolled, identity active, lockout window).
 *
 * Two SAFE support actions, each behind an AlertDialog (sensitive — they
 * change someone's credential / access posture):
 *  - Reset 2FA   → POST /admin/users/{IDENTITY_ID}/reset-2fa
 *  - Reactivate  → POST /admin/users/{MEMBERSHIP_USER_ID}/reactivate
 *                  (with an opt-in to ALSO lift the identity kill-switch)
 *
 * THE ID-SPACE NUANCE IS LOAD-BEARING: reset-2FA takes the identity_id
 * (2FA is identity-scoped); reactivate takes a membership user_id
 * (deactivation is per-tenant). Both come from the lookup row — never
 * conflated. The action handlers below pull each from its own field.
 */

import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertCircle,
  KeyRound,
  Loader2,
  Lock,
  Search,
  ShieldCheck,
  ShieldOff,
  UserCheck,
  UserSearch,
} from "lucide-react";
import { toast } from "sonner";
import { adminConsoleApi, toApiError } from "@/lib/api";
import type {
  AdminMembership,
  AdminPersonRow,
} from "@/types/admin-console";
import { PageHeader } from "@/components/shared/page-header";
import { EmptyState } from "@/components/shared/empty-state";
import { StatusBadge } from "@/components/shared/status-badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

export const Route = createFileRoute("/_authgated/superadmin/users")({
  component: UserLookupRoute,
});

function fmtDateTime(iso: string | null): string {
  if (!iso) return "never";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString("en-IN", {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function isLocked(person: AdminPersonRow): boolean {
  if (!person.locked_until) return false;
  const until = new Date(person.locked_until).getTime();
  return !Number.isNaN(until) && until > Date.now();
}

function UserLookupRoute() {
  const [draft, setDraft] = useState("");
  const [applied, setApplied] = useState<string>("");

  // The console offers one box; route a value containing "@" to the
  // exact-email param and everything else to the substring `q` param.
  const query = applied.includes("@")
    ? { email: applied }
    : { q: applied };

  const { data, isLoading, isFetching, error, refetch } = useQuery({
    queryKey: ["admin-user-lookup", applied],
    queryFn: ({ signal }) => adminConsoleApi.lookupUsers(query, signal),
    enabled: applied.length > 0,
  });

  function runSearch() {
    setApplied(draft.trim());
  }

  const items = data?.items ?? [];

  return (
    <>
      <PageHeader
        title="Users"
        description="Find a person across all tenants for a login ticket, then run a safe account fix."
      />

      <Card className="mb-4">
        <CardContent className="flex flex-col gap-3 py-5 sm:flex-row sm:items-end">
          <div className="flex-1 space-y-1.5">
            <Label htmlFor="u-search">Email or Name</Label>
            <Input
              id="u-search"
              placeholder="jane@example.com or Jane"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") runSearch();
              }}
            />
          </div>
          <Button
            onClick={runSearch}
            disabled={!draft.trim() || isFetching}
          >
            {isFetching ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Search className="h-4 w-4" />
            )}
            Search
          </Button>
        </CardContent>
      </Card>

      {applied.length === 0 ? (
        <EmptyState
          icon={UserSearch}
          title="Search for a Person"
          description="Enter an exact email or part of a name to find their identity and every workspace membership."
        />
      ) : error ? (
        <div className="flex flex-col items-start gap-3">
          <Alert variant="destructive" className="max-w-md">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Lookup failed</AlertTitle>
            <AlertDescription>
              {toApiError(error).message}
            </AlertDescription>
          </Alert>
          <Button variant="outline" onClick={() => refetch()}>
            Retry
          </Button>
        </div>
      ) : isLoading ? (
        <div className="space-y-3">
          <Skeleton className="h-48 w-full" />
          <Skeleton className="h-48 w-full" />
        </div>
      ) : items.length === 0 ? (
        <EmptyState
          icon={UserSearch}
          title="No Match"
          description="No identity matches that email or name. Try a different spelling or a substring."
        />
      ) : (
        <div className="space-y-4">
          {items.map((person) => (
            <PersonCard key={person.identity_id} person={person} />
          ))}
        </div>
      )}
    </>
  );
}

function PersonCard({ person }: { person: AdminPersonRow }) {
  const queryClient = useQueryClient();
  const locked = isLocked(person);

  function invalidate() {
    void queryClient.invalidateQueries({
      queryKey: ["admin-user-lookup"],
    });
  }

  const reset2faMutation = useMutation({
    mutationFn: () => adminConsoleApi.reset2FA(person.identity_id),
    onSuccess: (res) => {
      toast.success(res.message || "2FA reset");
      invalidate();
    },
    onError: (err) => toast.error(toApiError(err).message),
  });

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <CardTitle className="text-base">
              {person.first_name} {person.last_name}
            </CardTitle>
            <p className="text-sm text-muted-foreground">
              {person.email}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {person.is_platform && (
              <Badge
                variant="outline"
                className="border-transparent bg-primary/10 text-primary"
              >
                Platform
              </Badge>
            )}
            {person.totp_enabled ? (
              <Badge
                variant="outline"
                className="border-transparent bg-green-100 text-green-800"
              >
                <ShieldCheck className="mr-1 h-3 w-3" />
                2FA On
              </Badge>
            ) : (
              <Badge
                variant="outline"
                className="border-transparent bg-muted text-muted-foreground"
              >
                <ShieldOff className="mr-1 h-3 w-3" />
                2FA Off
              </Badge>
            )}
            {!person.is_active && (
              <Badge
                variant="outline"
                className="border-transparent bg-red-100 text-red-800"
              >
                Identity Disabled
              </Badge>
            )}
            {locked && (
              <Badge
                variant="outline"
                className="border-transparent bg-amber-100 text-amber-900"
              >
                <Lock className="mr-1 h-3 w-3" />
                Locked
              </Badge>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm sm:grid-cols-4">
          <dt className="text-muted-foreground">Identity ID</dt>
          <dd className="tabular-nums">{person.identity_id}</dd>
          <dt className="text-muted-foreground">Lockout</dt>
          <dd>{locked ? fmtDateTime(person.locked_until) : "—"}</dd>
        </dl>

        <Separator />

        {/* Memberships */}
        <div>
          <p className="mb-2 text-sm font-medium">
            Memberships ({person.memberships.length})
          </p>
          {person.memberships.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No tenant memberships (identity-only / platform account).
            </p>
          ) : (
            <ul className="space-y-2">
              {person.memberships.map((m) => (
                <MembershipRow
                  key={m.user_id}
                  membership={m}
                  onChanged={invalidate}
                />
              ))}
            </ul>
          )}
        </div>

        <Separator />

        {/* Identity-level safe action: Reset 2FA (identity_id) */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="text-sm">
            <p className="font-medium">Reset 2FA</p>
            <p className="text-muted-foreground">
              Clears TOTP for this person and signs out every session.
              They re-enrol on next login.
            </p>
          </div>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                variant="outline"
                disabled={reset2faMutation.isPending}
              >
                {reset2faMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <KeyRound className="h-4 w-4" />
                )}
                Reset 2FA
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>
                  Reset 2FA for {person.email}?
                </AlertDialogTitle>
                <AlertDialogDescription>
                  This clears all TOTP enrolment for identity #
                  {person.identity_id} and revokes every active session
                  across all their workspaces — a credential-posture
                  change, like a password reset. They will be prompted to
                  set up 2FA again on next login (mandatory for
                  super_admin / hr_manager). Use this only for a verified
                  account-recovery request.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  onClick={() => reset2faMutation.mutate()}
                >
                  Reset 2FA
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </CardContent>
    </Card>
  );
}

function MembershipRow({
  membership,
  onChanged,
}: {
  membership: AdminMembership;
  onChanged: () => void;
}) {
  const [alsoIdentity, setAlsoIdentity] = useState(false);

  const mutation = useMutation({
    mutationFn: () =>
      adminConsoleApi.reactivate(membership.user_id, alsoIdentity),
    onSuccess: (res) => {
      toast.success(
        res.identity_reactivated
          ? "Membership and identity reactivated"
          : "Membership reactivated",
      );
      onChanged();
    },
    onError: (err) => toast.error(toApiError(err).message),
  });

  const tenantLabel =
    membership.tenant_name ??
    (membership.tenant_slug ? membership.tenant_slug : "Platform");

  return (
    <li className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-border px-3 py-2.5">
      <div className="min-w-0 text-sm">
        <p className="font-medium">
          {tenantLabel}
          {membership.tenant_slug && (
            <span className="ml-2 font-normal text-muted-foreground">
              {membership.tenant_slug}
            </span>
          )}
        </p>
        <p className="text-xs text-muted-foreground">
          Role {membership.role} · membership #{membership.user_id} ·
          last login {fmtDateTime(membership.last_login_at)}
        </p>
      </div>
      <div className="flex items-center gap-3">
        {membership.is_active ? (
          <StatusBadge tone="success" label="Active" />
        ) : (
          <StatusBadge tone="danger" label="Deactivated" />
        )}
        {!membership.is_active && (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                disabled={mutation.isPending}
              >
                {mutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <UserCheck className="h-4 w-4" />
                )}
                Reactivate
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>
                  Reactivate this membership?
                </AlertDialogTitle>
                <AlertDialogDescription>
                  Restores membership #{membership.user_id} (role{" "}
                  {membership.role}) in <strong>{tenantLabel}</strong>.
                  The person regains access to this workspace.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <div className="px-1">
                <Label className="flex cursor-pointer items-start gap-3 rounded-md border border-border p-3 text-sm">
                  <Checkbox
                    checked={alsoIdentity}
                    onCheckedChange={(v) => setAlsoIdentity(v === true)}
                    className="mt-0.5"
                  />
                  <span>
                    <span className="block font-medium">
                      Also restore the identity
                    </span>
                    <span className="block text-muted-foreground">
                      Lifts the account-wide kill-switch too (sends{" "}
                      <code className="text-xs">?identity=1</code>). Use
                      when the whole person was disabled, not just this
                      one workspace.
                    </span>
                  </span>
                </Label>
              </div>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={() => mutation.mutate()}>
                  Reactivate
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
      </div>
    </li>
  );
}
